import type { Editor } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';

type CalloutType = 'info' | 'success' | 'warning' | 'danger' | 'muted';

function getInsertion(editor: Editor, type: CalloutType) {
    const { selection, schema, doc } = editor.state;
    const calloutType = schema.nodes.callout;
    const paragraphType = schema.nodes.paragraph;
    if (!editor.isEditable || !calloutType || !paragraphType || !(selection instanceof TextSelection)) return null;

    const { $from, $to } = selection;
    if (!$from.parent.isTextblock || !$to.parent.isTextblock || $from.depth === 0 || $from.depth !== $to.depth) return null;
    const depth = $from.depth;
    const container = $from.node(depth - 1);
    // Do not extract content across cells, panels, list items, or other containers.
    if ($from.start(depth - 1) !== $to.start(depth - 1)) return null;

    const from = $from.before(depth);
    const to = $to.after(depth);
    let callout;
    let content: Fragment;
    let calloutOffset = 0;

    if (!selection.empty) {
        // Partial text within one block wraps that entire block. Multiple blocks
        // must be selected completely and belong to the same immediate container.
        if (!$from.sameParent($to) && ($from.parentOffset !== 0 || $to.parentOffset !== $to.parent.content.size)) return null;
        const selectedBlocks = doc.slice(from, to).content;
        if (!calloutType.validContent(selectedBlocks)) return null;
        callout = calloutType.create({ type }, selectedBlocks);
        content = Fragment.from(callout);
    } else {
        const block = $from.parent;
        const offset = $from.parentOffset;
        callout = calloutType.create({ type }, paragraphType.create());
        const before = offset > 0 ? block.copy(block.content.cut(0, offset)) : null;
        const after = offset < block.content.size ? block.copy(block.content.cut(offset)) : null;
        // Empty blocks are replaced; the beginning/end have just one surviving
        // block, and the middle keeps both halves with their attributes and marks.
        content = Fragment.fromArray([
            ...(before ? [before] : []), callout, ...(after ? [after] : []),
        ]);
        calloutOffset = before?.nodeSize ?? 0;
    }

    if (!container.canReplace($from.index(depth - 1), $to.indexAfter(depth - 1), content)) return null;
    return { from, to, content, calloutOffset, callout };
}

export function canInsertCallout(editor: Editor): boolean {
    return getInsertion(editor, 'info') !== null;
}

export function insertCallout(editor: Editor, type: CalloutType): boolean {
    const insertion = getInsertion(editor, type);
    if (!insertion) return false;

    const { from, to, content, calloutOffset, callout } = insertion;
    const tr = editor.state.tr.replaceWith(from, to, content);
    const calloutStart = from + calloutOffset;
    const $after = tr.doc.resolve(calloutStart + callout.nodeSize);
    // Leave a place to continue writing outside a callout at the container's end.
    const paragraph = editor.schema.nodes.paragraph.create();
    if ($after.parentOffset === $after.parent.content.size && $after.parent.canReplace($after.index(), $after.index(), Fragment.from(paragraph))) {
        tr.insert($after.pos, paragraph);
    }
    tr.setSelection(TextSelection.near(tr.doc.resolve(calloutStart + 1), 1)).scrollIntoView();
    editor.view.dispatch(tr);
    editor.view.focus();
    return true;
}
